import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { RequestCollectionScoringBody } from '../../generated';
import { requestCollectionScoringV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsPost } from '../../generated';
import type { CollectionScoring } from '../../queries/list-collection-scorings';
import { toCollectionScoring } from '../../queries/list-collection-scorings';

/*
 * POST /v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/scorings: scores the collection's
 * images under a product category against one benchmark, a custom one by `benchmark_id` or a Vizit one by
 * `retailer`. Answers `ready` when the pair is already scored and `pending` otherwise; a repeat request for a pending
 * pair returns the same scoring. The API refuses a custom benchmark under its `minimum_images` with
 * `BENCHMARK_TOO_SMALL`. The answer is the generated `CollectionScoringView`; only its summary fields are mapped.
 * Tests answer this path with `tests/mocks/collection-scoring-handlers.ts`.
 */

export type RequestCollectionScoringMutationVariables = {
  workspace_id: string;
  collection_id: string;
} & RequestCollectionScoringBody;

export type RequestCollectionScoringMutation = CollectionScoring;

async function requestCollectionScoring(variables: RequestCollectionScoringMutationVariables) {
  const { workspace_id, collection_id, product_category_id, benchmark_id, retailer } = variables;
  const { data } = await requestCollectionScoringV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdScoringsPost(
    {
      path: { workspace_id, collection_id },
      // Only the benchmark field the caller set goes on the wire; `undefined` is dropped when serialized.
      body: { product_category_id, benchmark_id: benchmark_id ?? undefined, retailer: retailer ?? undefined },
      throwOnError: true,
    },
  );
  return toCollectionScoring(data);
}

export const useRequestCollectionScoringMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<RequestCollectionScoringMutation, TError, RequestCollectionScoringMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<RequestCollectionScoringMutation, TError, RequestCollectionScoringMutationVariables, TContext>({
    mutationKey: ['RequestCollectionScoring'],
    mutationFn: requestCollectionScoring,
    ...options,
  });
};

useRequestCollectionScoringMutation.fetcher = (variables: RequestCollectionScoringMutationVariables) => () =>
  requestCollectionScoring(variables);
